import L from "leaflet";
import type { CatalogueEntry } from "../catalogue/types";
import type { HostLayerDefinition } from "../core/options";

export const hostGroupKey = "host";

export type HostKind = "geojson" | "leaflet" | "track";

export function hostKindOf(definition: HostLayerDefinition): HostKind {
  if (definition.kind) return definition.kind;
  if (definition.track) return "track";
  if (definition.geojson) return "geojson";

  return "leaflet";
}

export function hostDefinitionOf(entry: CatalogueEntry): HostLayerDefinition | null {
  const value = entry.settings.host;

  return value && typeof value === "object" ? (value as HostLayerDefinition) : null;
}

export function hostEntry(definition: HostLayerDefinition, groupName: string, order: number): CatalogueEntry {
  const kind = hostKindOf(definition);

  return {
    id: -(order + 1),
    idName: definition.key,
    key: definition.key,
    root: "layers",
    kind: "geoobject",
    name: definition.title,
    order,
    visible: true,
    parentKey: definition.group ?? hostGroupKey,
    path: [definition.group ?? hostGroupKey],
    pathNames: [groupName],
    depth: 1,
    selection: "multi",
    children: [],
    collapsed: false,
    defaultSelected: definition.selected ?? true,
    opacity: 1,
    minZoom: null,
    maxZoom: null,
    attribution: null,
    bounds: null,
    style: definition.color ? { pen: { color: definition.color, width: 3, type: null, border: null }, brush: { color: definition.color, style: null, opacity: 0.15 }, icon: null } : null,
    tile: null,
    special: null,
    source: null,
    layerState: null,
    tiles: null,
    vector: null,
    legend: definition.legend ?? null,
    settings: { host: definition, hostKind: kind },
    rawValue: kind,
    problems: [],
    origin: "host"
  };
}

type BoundedLayer = L.Layer & { getBounds: () => L.LatLngBounds };

function hasBounds(layer: L.Layer): layer is BoundedLayer {
  return typeof (layer as BoundedLayer).getBounds === "function";
}

export function layerBounds(layer: L.Layer): L.LatLngBounds | null {
  if (layer instanceof L.Marker || layer instanceof L.CircleMarker) return L.latLngBounds([layer.getLatLng()]);

  if (hasBounds(layer)) {
    const bounds = layer.getBounds();

    return bounds.isValid() ? bounds : null;
  }

  if (layer instanceof L.LayerGroup) {
    const bounds = L.latLngBounds([]);

    layer.eachLayer(child => {
      const childBounds = layerBounds(child);

      if (childBounds !== null) bounds.extend(childBounds);
    });

    return bounds.isValid() ? bounds : null;
  }

  return null;
}
